import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { salaryInsightsService } from '../../services/salaryInsightsService';
import { occupationService } from '../../services/occupationService';
import { SalaryWidget } from './components/SalaryWidget';
import { ZipCodeManager } from './components/ZipCodeManager';
import { ExperienceLevelSelector } from './components/ExperienceLevelSelector';
import { PremiumUpgradePrompt } from './components/PremiumUpgradePrompt';
import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import AdSense from '../../components/AdSense';

const SalaryInsights = () => {
  const { user, userProfile } = useAuth();
  const [occupation, setOccupation] = useState(null);
  const [salaryData, setSalaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // ZIP management state
  const [currentZip, setCurrentZip] = useState('');
  const [targetZip, setTargetZip] = useState('');
  const [activeZip, setActiveZip] = useState('current');
  
  // Experience level state
  const [selectedExperience, setSelectedExperience] = useState('median');

  // Add missing handler functions
  const handleAuthRequired = () => {
    // Handle authentication requirement
    window.location.href = '/authentication';
  };

  const handleLogout = () => {
    // Handle logout functionality
    // This would typically call a logout method from AuthContext
    console.log('Logout requested');
  };

  useEffect(() => {
    loadUserData();
  }, [user, userProfile]);

  const loadUserData = async () => {
    if (!user || !userProfile) return;

    try {
      // Load user's ZIP codes
      const zipCodes = await salaryInsightsService?.getUserZipCodes(user?.id);
      setCurrentZip(zipCodes?.currentZip);
      setTargetZip(zipCodes?.targetZip);

      // Load user's occupation if selected
      if (userProfile?.occupation_id) {
        const occupationData = await occupationService?.getOccupationById(userProfile?.occupation_id);
        setOccupation(occupationData);

        // Auto-load salary data if we have both occupation and ZIP
        const zipToUse = zipCodes?.currentZip || zipCodes?.targetZip;
        if (zipToUse && salaryInsightsService?.validateZipCode(zipToUse)) {
          loadSalaryData(userProfile?.occupation_id, zipToUse);
        }
      }

      // Set experience level from user profile
      if (userProfile?.seniority_level) {
        const levelMapping = {
          'entry': 'entry',
          'junior': 'entry',
          'mid': 'median',
          'senior': 'experienced',
          'executive': 'experienced'
        };
        setSelectedExperience(levelMapping?.[userProfile?.seniority_level] || 'median');
      }
    } catch (err) {
      console.error('Error loading user data:', err);
      setError('Failed to load user data');
    }
  };

  const loadSalaryData = async (occupationId, zipCode) => {
    if (!occupationId || !zipCode) return;

    setLoading(true);
    setError('');

    try {
      const result = await salaryInsightsService?.getSalaryInsights(
        occupationId,
        zipCode,
        userProfile?.role || 'free'
      );
      setSalaryData(result);
    } catch (err) {
      console.error('Error loading salary data:', err);
      setError('Failed to load salary insights. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleZipUpdate = async (type, zipCode) => {
    if (!user) return;

    try {
      if (type === 'current') {
        await salaryInsightsService?.updateCurrentZip(user?.id, zipCode);
        setCurrentZip(zipCode);
      } else {
        await salaryInsightsService?.updateTargetZip(user?.id, zipCode);
        setTargetZip(zipCode);
      }

      // Reload salary data if occupation is selected
      if (occupation?.id && zipCode) {
        loadSalaryData(occupation?.id, zipCode);
      }
    } catch (err) {
      console.error('Error updating ZIP:', err);
      setError('Failed to update ZIP code');
    }
  };

  const handleExperienceChange = (level) => {
    setSelectedExperience(level);
  };

  const getActiveZipCode = () => {
    if (activeZip === 'current') return currentZip;
    if (activeZip === 'target') return targetZip;
    return '';
  };

  const getFilteredSalaryData = () => {
    if (!salaryData?.results) return [];
    return salaryData?.results?.filter(item => item?.experience_level === selectedExperience);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Salary Insights</h1>
            <p className="text-gray-600 mb-2">Sign in to access personalized salary data by ZIP code</p>
            <a
              href="/authentication"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-primary/90"
            >
              Sign In
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Salary Insights - CareerLift</title>
        <meta name="description" content="Get comprehensive salary insights, market analysis, and compensation data for your profession with CareerLift's advanced analytics." />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <Header onAuthRequired={handleAuthRequired} onLogout={handleLogout} />
        
        <main className="flex-1 pt-32 pb-16" style={{ paddingTop: 'calc(8rem + 30px)' }}>
          <div className="max-w-5xl mx-auto">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              {/* Page Header */}
              <div className="text-center mb-2">
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <Icon name="DollarSign" size={32} className="text-primary" />
                  <h1 className="text-3xl font-bold text-foreground">
                    Salary Insights
                  </h1>
                </div>
                <p className="text-muted-foreground">
                  Discover salary ranges for {occupation?.title || 'your occupation'} by location
                </p>
              </div>

              {/* Page Audio */}
              <div className="flex justify-center mb-2">
                <audio
                  src="/assets/audio/Salary_Insights.mp3"
                  controls
                  preload="metadata"
                  className="h-8 max-w-xs"
                />
              </div>

              {/* AdSense - Banner for Free Users */}
              {userProfile?.role === 'free' && (
                <div className="mb-2">
                  <AdSense 
                    adSlot="1234567890"
                    adFormat="auto"
                    className="w-full"
                  />
                </div>
              )}

              <div className="space-y-8">

            {/* No occupation selected */}
            {!occupation && (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Select Your Occupation</h2>
                <p className="text-gray-600 mb-4">
                  Please complete your career assessment to see salary insights for your occupation.
                </p>
                <a
                  href="/career-assessment-setup"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90"
                >
                  Complete Assessment
                </a>
              </div>
            )}

            {/* Controls */}
            {occupation && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-2">
                {/* ZIP Code Management */}
                <div className="lg:col-span-2">
                  <ZipCodeManager
                    currentZip={currentZip}
                    targetZip={targetZip}
                    activeZip={activeZip}
                    onActiveZipChange={setActiveZip}
                    onZipUpdate={handleZipUpdate}
                    loading={loading}
                  />
                </div>

                {/* Experience Level */}
                <div>
                  <ExperienceLevelSelector
                    selectedLevel={selectedExperience}
                    onLevelChange={handleExperienceChange}
                    userSeniorityLevel={userProfile?.seniority_level}
                  />
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Salary Widgets */}
            {occupation && getActiveZipCode() && (
              <div className="space-y-6">
                {/* Premium upgrade prompt for free users */}
                {userProfile?.role === 'free' && (
                  <PremiumUpgradePrompt 
                    currentZip={getActiveZipCode()}
                    occupation={occupation}
                  />
                )}

                {/* Salary Data Display */}
                <SalaryWidget
                  salaryData={getFilteredSalaryData()}
                  loading={loading}
                  occupation={occupation}
                  zipCode={getActiveZipCode()}
                  experienceLevel={selectedExperience}
                  userRole={userProfile?.role || 'free'}
                />
              </div>
            )}

            {/* Empty state when no ZIP selected */}
            {occupation && !getActiveZipCode() && !loading && (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                  <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Enter Your ZIP Code</h3>
                <p className="text-gray-600">
                  Add your current or target ZIP code to see salary insights for your area.
                </p>
              </div>
            )}
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default SalaryInsights;